import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, combineLatest, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PatientAddress } from 'src/app/models/patient.model';
import { AddressUSPSValidatorService } from 'src/app/services/address-validator/address-uspsvalidator.service';
import { DataService } from 'src/app/services/data.service';
import { DeviceOrderingService } from 'src/app/services/device-ordering/device-ordering.service';
import { DeviceVO } from 'src/app/services/device-ordering/value-objects/order.vo';
import { PatientDetailService } from 'src/app/services/patient-detail.service';
import { SnackService } from 'src/app/services/snack.service';
import { PatientService } from 'src/app/services/patient.service';
@Component({
  selector: 'app-new-device-modal',
  templateUrl: './new-device-modal.component.html',
  styleUrls: ['./new-device-modal.component.scss'],
})
export class NewDeviceModalComponent implements OnInit, OnDestroy {
  addressForm: UntypedFormGroup;
  termsForm: UntypedFormGroup;
  showForm = true;
  addressValidated$: Observable<boolean>;
  loading = false;
  primaryAddress: PatientAddress;
  unsubscribe = new Subject<void>();
  showParagraph = true;
  public selectedManufacturer = '';
  public selectedDevice: DeviceVO;
  correctionMessage = '';
  states = [];
  deviceTypes = [];
  arePatientAddressesAvailable = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private dialogRef: MatDialogRef<NewDeviceModalComponent>,
    private dialog: MatDialog,
    public deviceService: DeviceOrderingService,
    private dataService: DataService,
    private fb: UntypedFormBuilder,
    private addressValidator: AddressUSPSValidatorService,
    private snackService: SnackService,
    private patientDetailService: PatientDetailService,
    private patientService: PatientService
  ) {}

  ngOnInit(): void {
    this.states = this.dataService.statesWithabbreviation;
    this.initializeNewOrderForm();
    if (this.data) {
      this.patientDetailService.addresses$.pipe(takeUntil(this.unsubscribe)).subscribe((addresses) => {
        if (addresses.length > 0) {
          this.arePatientAddressesAvailable = true;
          this.primaryAddress = addresses[0];
          this.addressForm.patchValue({
            address: this.primaryAddress.address ?? null,
            address2: this.primaryAddress.address2 ?? null,
            city: this.primaryAddress.city ?? null,
            state: this.primaryAddress.state ?? null,
            postalCode: this.primaryAddress?.postalCode
              ? String(this.primaryAddress.postalCode)?.trim()
              : this.primaryAddress?.zip ? String(this.primaryAddress.zip).trim() : null,
          });
          this.listenAddressUpdates();
        }
      });
    }
    this.deviceService.getAllDevices().pipe(takeUntil(this.unsubscribe)).subscribe();
    this.addressForm
      .get('type')
      .valueChanges.pipe(
        filter((device) => !!device?.sku_display),
        switchMap((device) =>
          this.deviceService
            .duplicateDeviceOrders(device?.sku, this.data?.patient?.id)
            .pipe(catchError(() => of(false)))
        ),
        tap((isRequired) => {
          const commentsControl = this.addressForm.get('comments');
          if (isRequired) {
            commentsControl?.setValidators([Validators.required]);
          } else {
            commentsControl?.clearValidators();
          }
          commentsControl?.updateValueAndValidity({ emitEvent: false });
        })
      )
      .subscribe();
  }

  initializeNewOrderForm(): void {
    this.addressForm = this.fb.group({
      firstName: [
        this.data?.patient && this.data?.patient?.firstName ? this.data?.patient?.firstName : null,
        Validators.required,
      ],
      lastName: [
        this.data?.patient && this.data?.patient?.lastName ? this.data?.patient?.lastName : null,
        Validators.required,
      ],
      address: [this.primaryAddress?.address ?? null, Validators.required],
      address2: [this.primaryAddress?.address2 ?? null],
      city: [this.primaryAddress?.city ?? null, Validators.required],
      state: [this.primaryAddress?.state ?? null, Validators.required],
      postalCode: [
        this.primaryAddress?.postalCode?.toString().trim() ?? this.primaryAddress?.zip?.toString().trim() ?? '',
        Validators.pattern(/^\d{5}$/),
      ],
      manufacturer: [''],
      type: [''],
      clientId: [this.data?.patient?.client_id ?? ''],
      comments: [''],
    });
    this.termsForm = this.fb.group({
      terms: ['', Validators.required],
    });
    this.addressForm.get('postalCode').markAllAsTouched();
  }

  handleManufacturerSelected() {
    this.deviceService.getDevicesByManufacturer(this.selectedManufacturer).pipe(take(1)).subscribe();
  }

  orderDevice() {
    const device = this.addressForm.getRawValue().type;
    this.dialogRef.close(device);
  }

  onSubmit(): void {
    this.loading = true;
    this.onValidateAddress()
      .pipe(
        switchMap((isValid) => {
          if (isValid) {
            if (!this.arePatientAddressesAvailable) {
              const addressData = {
                address: this.addressForm.get('address').value,
                address2: this.addressForm.get('address2').value,
                city: this.addressForm.get('city').value,
                state: this.addressForm.get('state').value,
                zip: this.addressForm.get('postalCode').value,
                primary: true,
              };
              return from(this.patientService.handlePatientAddress(true, addressData, this.data?.patient?.id)).pipe(
                map(() => true)
              );
            }
            return of(true);
          }
          return of(false);
        })
      )
      .subscribe((isValid) => {
        if (isValid) {
          this.showForm = false;
        }
        this.loading = false;
      });
  }

  onValidateAddressClick(): void {
    this.onValidateAddress().pipe(takeUntil(this.unsubscribe)).subscribe();
  }

  onValidateAddress(): Observable<boolean> {
    this.loading = true;
    this.addressValidated$ = of(false);
    return this.addressValidator.validateAndApply(this.addressForm).pipe(
      map((result) => {
        this.correctionMessage = result.correctionMessage;
        this.addressValidated$ = of(result.valid);
        this.loading = false;
        return result.valid;
      })
    );
  }

  acceptTerms(): void {
    this.dialogRef.close(this.addressForm.value);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private listenAddressUpdates(): void {
    combineLatest([
      this.addressForm.get('address').valueChanges,
      this.addressForm.get('address2').valueChanges,
      this.addressForm.get('state').valueChanges,
      this.addressForm.get('city').valueChanges,
      this.addressForm.get('postalCode').valueChanges,
    ])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(([address, address2, state, city, postalCode]) => (this.addressValidated$ = of(false)));
  }
}
